import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AuthService, EventsService, CustomerService, ResourceService, EventRequestsService } from "../../services";
import { Spinner, Breadcrumb, BreadcrumbItem, Tabs, Tab, Dropdown } from "react-bootstrap";
import { Columns, Download, Filter, PencilSquare, PersonSquare, Plus } from "react-bootstrap-icons";
import ReactPaginate from "react-paginate";
import { ManageTable, Export } from "../../shared/components";
import { readPageUiObject, writePageUiObject } from "../../shared/pageFilterPersistence";
import { RESOURCE_TYPE_TEXT } from "../../shared/constants/resource.constant";
import { pickRawSeatStorageForChart } from "../../shared/seatingTableDisplay";
import { navigateToMedicalCreateFromRequest } from "../../shared/utils/medicalCreateEventPayload";
import { usePageScrollPinnedTableHeader } from "../../shared/hooks";

const REQUESTS_PER_PAGE_DEFAULT = 25;

const formatResourceAddress = (resource) => {
  if (!resource) return '';
  const legacy = `${resource.address || ''}`.trim();
  if (legacy) return legacy;
  const line = [resource.address_line_1, resource.address_line_2].filter(Boolean).join(', ');
  const cityState = [resource.city, resource.state].filter(Boolean).join(', ');
  const parts = [line, cityState, resource.zipcode].filter(Boolean);
  return parts.join(' ');
};

/** Legacy rows may still store "Emergency"; treat both as urgent. */
const isUrgentRequestType = (type) => {
  const t = `${type || ''}`.trim().toLowerCase();
  return t === 'urgent' || t === 'emergency';
};

const formatRequestTypeDisplay = (type) => {
  const raw = `${type || ''}`.trim();
  if (!raw) return '';
  if (isUrgentRequestType(raw)) return 'Urgent';
  return raw;
};

const EventRequestList = () => {
  const navigate = useNavigate();
  const [eventRequests, setEventRequests] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [resources, setResources] = useState([]);
  const [sorting, setSorting] = useState({key: '', order: ''});
	const [showDone, setShowDone] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [requestTypeFilter, setRequestTypeFilter] = useState('');
  const [transportationSupportFilter, setTransportationSupportFilter] = useState('');
  const [newPatientFilter, setNewPatientFilter] = useState('');
  const [itemOffset, setItemOffset] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(REQUESTS_PER_PAGE_DEFAULT);
  const [columns, setColumns] = useState([
    {
      key: 'customer_display',
      label:'Customer Name',
      show: true
    },
    {
      key: 'source',
      label: 'Request By',
      show: true
    },
    {
      key: 'type',
      label: 'Request Type',
      show: true
    },
    {
      key: 'provider',
      label: 'Provider',
      show: true
    },
    {
      key: 'provider_type',
      label: 'Provider Type',
      show: true
    },
    {
      key: 'provider_phone',
      label: 'Provider Phone',
      show: true
    },
    {
      key: 'provider_address',
      label: 'Provider Address',
      show: true
    },
    {
      key: 'transportation',
      label: 'Transportation Support',
      show: true
    },
    {
      key: 'symptom',
      label: 'Request Note',
      show: true
    },
    {
      key: 'np',
      label: 'New Patient',
      show: true
    },
    {
      key: 'upload',
      label: 'Created by',
      show: true
    },
    {
      key: 'status',
      label: 'Status',
      show: true
    },
    {
      key: 'create_date',
      label: 'Create Date',
      show: true
    }
  ]);
  const [filtersHydrated, setFiltersHydrated] = useState(false);
  useEffect(() => {
    const saved = readPageUiObject("event_request_list");
    if (saved) {
      if (typeof saved.showDone === "boolean") setShowDone(saved.showDone);
      if (typeof saved.keyword === "string") setKeyword(saved.keyword);
      if (typeof saved.requestTypeFilter === "string") setRequestTypeFilter(saved.requestTypeFilter);
      if (typeof saved.transportationSupportFilter === "string")
        setTransportationSupportFilter(saved.transportationSupportFilter);
      if (typeof saved.newPatientFilter === "string") setNewPatientFilter(saved.newPatientFilter);
      if (saved.sorting && typeof saved.sorting === "object") setSorting(saved.sorting);
      const parsedPerPage = Number(saved.itemsPerPage);
      if (Number.isFinite(parsedPerPage)) {
        setItemsPerPage(Math.min(200, Math.max(5, parsedPerPage)));
      }
    }
    setFiltersHydrated(true);
  }, []);

  const tableFiltersSnapshot = useMemo(
    () => ({
      showDone,
      keyword,
      requestTypeFilter,
      transportationSupportFilter,
      newPatientFilter,
      sorting,
      itemsPerPage,
    }),
    [showDone, keyword, requestTypeFilter, transportationSupportFilter, newPatientFilter, sorting, itemsPerPage]
  );
  useEffect(() => {
    if (!filtersHydrated) return;
    const t = setTimeout(() => {
      writePageUiObject("event_request_list", tableFiltersSnapshot);
    }, 400);
    return () => clearTimeout(t);
  }, [filtersHydrated, tableFiltersSnapshot]);

  useEffect(() => {
    if (!AuthService.canViewAppointmentRequests()) {
      window.alert('You haven\'t login yet OR this user does not have access to this page. Please change an admin account to login.')
      AuthService.logout();
      navigate(`/login`);
    }
    CustomerService.getAllCustomers().then((data) => {
      setCustomers(data.data);
    })
    ResourceService.getAll().then(data => {
      setResources(data.data);
    }) 
  }, []);

  useEffect(() => {
    if (customers?.length > 0 && resources?.length>0) {
      EventRequestsService.getAllEventRequests().then((data) => {
				setEventRequests(data.data);
			})
    }
  }, [resources, customers]);

  const resourceById = useMemo(() => {
    const map = new Map();
    (resources || []).forEach((resource) => {
      if (resource?.id != null) map.set(String(resource.id), resource);
    });
    return map;
  }, [resources]);

  const sourceLabelByValue = useMemo(() => {
    const map = {};
    (EventRequestsService.sourceList || []).forEach((item) => {
      if (item?.value != null) map[item.value] = item.label;
    });
    return map;
  }, []);

  const getResourceForRequest = (eventRequest) => {
    if (!eventRequest?.resource) return null;
    return resourceById.get(String(eventRequest.resource)) || null;
  };

  const getProviderDisplay = (eventRequest) => {
    const res = getResourceForRequest(eventRequest);
    const name = `${eventRequest?.resource_display || res?.name || res?.office_name || ''}`.trim();
    return name || '-';
  };

  const getProviderPhoneDisplay = (eventRequest) => {
    const res = getResourceForRequest(eventRequest);
    const phone = `${res?.phone || ''}`.trim();
    return phone || '-';
  };

  const getProviderAddressDisplay = (eventRequest) => {
    const res = getResourceForRequest(eventRequest);
    const addr = formatResourceAddress(res);
    return addr || '-';
  };

  const getProviderTypeDisplay = (eventRequest) => {
    const res = getResourceForRequest(eventRequest);
    if (!res) return '-';
    if (res.type === 'other' && res.type_other) {
      const base = RESOURCE_TYPE_TEXT[res.type] || res.type;
      return base ? `${base} (${res.type_other})` : res.type_other;
    }
    return RESOURCE_TYPE_TEXT[res.type] || res.type || '-';
  };

  const filterRequestsFun = (item, statusParam, keywordParam) => {
    const normalizedKeyword = `${keywordParam || ''}`.toLowerCase().trim();
    const normalizedRequestTypeFilter = `${requestTypeFilter || ''}`.toLowerCase().trim();
    const normalizedTransportationFilter = `${transportationSupportFilter || ''}`.toLowerCase().trim();
    const normalizedNewPatientFilter = `${newPatientFilter || ''}`.toLowerCase().trim();
    const statusLabel = getStatusLabel(item?.status || '');
    const completeBy = item?.edit_history?.[item?.edit_history?.length - 1]?.employee || '';
    const itemRequestType = `${item?.type || ''}`.toLowerCase().trim();
    const itemRequestTypeDisplay = formatRequestTypeDisplay(item?.type).toLowerCase().trim();
    const itemTransportation = `${item?.transportation || ''}`.toLowerCase().trim();
    const itemNewPatient = `${item?.np || ''}`.toLowerCase().trim();
    const matchesRequestType =
      !normalizedRequestTypeFilter ||
      itemRequestType === normalizedRequestTypeFilter ||
      itemRequestTypeDisplay === normalizedRequestTypeFilter ||
      (normalizedRequestTypeFilter === 'urgent' && isUrgentRequestType(item?.type));

    const isMatchingStructuredFilters =
      matchesRequestType &&
      (!normalizedTransportationFilter || itemTransportation === normalizedTransportationFilter) &&
      (!normalizedNewPatientFilter || itemNewPatient === normalizedNewPatientFilter);

    if (!normalizedKeyword) {
      return item?.status === statusParam && isMatchingStructuredFilters;
    }
    return (
      item?.status === statusParam &&
      isMatchingStructuredFilters &&
      (
        item?.customer_display?.toLowerCase()?.includes(normalizedKeyword) ||
        item?.source?.toLowerCase()?.includes(normalizedKeyword) ||
        item?.type?.toLowerCase()?.includes(normalizedKeyword) ||
        formatRequestTypeDisplay(item?.type).toLowerCase().includes(normalizedKeyword) ||
        item?.transportation?.toLowerCase()?.includes(normalizedKeyword) ||
        item?.symptom?.toLowerCase()?.includes(normalizedKeyword) ||
        item?.np?.toLowerCase()?.includes(normalizedKeyword) ||
        item?.upload?.toLowerCase()?.includes(normalizedKeyword) ||
        statusLabel.toLowerCase().includes(normalizedKeyword) ||
        completeBy.toLowerCase().includes(normalizedKeyword) ||
        getProviderDisplay(item).toLowerCase().includes(normalizedKeyword) ||
        getProviderTypeDisplay(item).toLowerCase().includes(normalizedKeyword) ||
        getProviderPhoneDisplay(item).toLowerCase().includes(normalizedKeyword) ||
        getProviderAddressDisplay(item).toLowerCase().includes(normalizedKeyword)
      )
    );
  }
 
  const redirectToAdmin = () => {
    navigate(`/dashboard/dashboard`)
  }


  const goToCreateNew = () => {
    navigate(`/medical/event-request`)
  }

  const showDoneItems = (value) => {
		setShowDone(value === 'completedRequest');
		setKeyword('');
		setSorting({key: '', order: ''});
		setItemOffset(0);
		setCurrentPage(1);
	}

	const deleteItem = (id) => {
		EventRequestsService.updateEventRequest(id, {status: 'inactive'}).then(() => {
			EventRequestsService.getAllEventRequests().then((data) => {
				setEventRequests(data.data);
			})
		})
	}

	const confirmEventRequest = (eventRequest) => {
		EventRequestsService.updateEventRequest(eventRequest.id, {status: 'done', edit_history:[{ employee: localStorage.getItem('user') && JSON.parse(localStorage.getItem('user'))?.name, date: new Date() }]}).then(() => {
			EventRequestsService.getAllEventRequests().then((data) => {
				setEventRequests(data.data);
			})
		})
	}

	const createNewEvent = (eventRequest) => {
		const customer = customers.find((c) => `${c.id}` === `${eventRequest?.customer_id}`);
		const resource = getResourceForRequest(eventRequest);
		const newEventData = {
      customer: eventRequest?.customer_id,
      resource: eventRequest?.resource,
      client_name: customer?.name,
      client_pharmacy: customer?.pharmacy,
      client_pcp: customer?.care_provider,
      client_birth_date: customer?.birth_date,
      client_seating: pickRawSeatStorageForChart(customer) || customer?.seating,
      resource_type: resource?.type,
      resource_name: resource?.name || eventRequest?.resource_display || "",
      resource_display: eventRequest?.resource_display || "",
      resource_phone: resource?.phone,
      resource_contact: resource?.contact,
      resource_address: resource?.address,
      resource_city: resource?.city,
      resource_state: resource?.state,
      // legacy fields end
      // We still wanna keep the legacy fields below
      notes: eventRequest?.symptom,
      other: eventRequest?.symptom,
      disability: customer?.disability? 'yes' : 'no',
      trans_method: eventRequest?.transportation,
      new_patient: eventRequest?.np?.toLowerCase()
    }
    
    const newEvent = {
      data: newEventData,
      notes: eventRequest?.symptom,
      start_time: new Date(),
      type: 'medical',
      source_type: resource?.type,
      source_uuid: resource?.id || eventRequest?.resource,
      source_name: resource?.name || eventRequest?.resource_display || "",
      target_type: customer?.type,
      target_uuid: customer?.id,
      target_name: customer?.name,
      create_by: localStorage.getItem('user') && JSON.parse(localStorage.getItem('user'))?.name,
      create_date: new Date(),
      edit_by: localStorage.getItem('user') && JSON.parse(localStorage.getItem('user'))?.name,
      edit_date: new Date(),
      edit_history:[{ employee: localStorage.getItem('user') && JSON.parse(localStorage.getItem('user'))?.name, date: new Date() }]
    }

		navigateToMedicalCreateFromRequest(navigate, newEvent);
	}

  const saveComments = (id) => {
    const textarea = document.getElementById(id);
    const currentComment = `${textarea?.value || ''}`.trim();
    if (!currentComment) return;

    const existingComments = eventRequests?.find((request) => request.id === id)?.notes || [];
    const newComments = [...existingComments, {
      content: currentComment,
      author: localStorage.getItem('user') && JSON.parse(localStorage.getItem('user'))?.name
    }];
    EventRequestsService.updateEventRequest(id, {
      notes: newComments
    }).then(() => {
      EventRequestsService.getAllEventRequests().then((data) => {
        setEventRequests(data.data);
        if (textarea) textarea.value = '';
      });
    });
  }

  const getSortingImg = (key) => {
    return sorting.key === key ? (sorting.order === 'asc' ? 'up_arrow' : 'down_arrow') : 'default';
  }

  const sortTableWithField = (key) => {
    let newSorting = {
      key,
      order: 'asc',
    }
    
    if (sorting.key === key && sorting.order === 'asc') {
      newSorting = {...newSorting, order: 'desc'};
    }
    setSorting(newSorting);
  }

	const getStatusLabel = (status) => status === 'active' ? 'In Progress' : (status==='done' ? 'Done' : 'Deleted')
  const activeTabStatus = showDone ? 'done' : 'active';

  const orderedEventRequests = useMemo(() => {
    const pinUrgentFirst = (list) =>
      [...list].sort((a, b) => {
        const aUrgent = isUrgentRequestType(a?.type) ? 0 : 1;
        const bUrgent = isUrgentRequestType(b?.type) ? 0 : 1;
        return aUrgent - bUrgent;
      });

    if (!sorting.key) {
      return pinUrgentFirst(eventRequests);
    }
    const sortVal = (item) => {
      const res = item?.resource ? resourceById.get(String(item.resource)) : null;
      switch (sorting.key) {
        case 'provider':
          return `${item?.resource_display || res?.name || res?.office_name || ''}`.trim();
        case 'provider_type': {
          const t = getProviderTypeDisplay(item);
          return t === '-' ? '' : t;
        }
        case 'provider_phone':
          return `${res?.phone || ''}`;
        case 'provider_address':
          return formatResourceAddress(res);
        case 'type':
          return formatRequestTypeDisplay(item?.type);
        default:
          return `${item?.[sorting.key] ?? ''}`;
      }
    };
    const sorted = [...eventRequests].sort((a, b) => {
      const aUrgent = isUrgentRequestType(a?.type) ? 0 : 1;
      const bUrgent = isUrgentRequestType(b?.type) ? 0 : 1;
      if (aUrgent !== bUrgent) return aUrgent - bUrgent;
      return sortVal(a).localeCompare(sortVal(b), undefined, { numeric: true, sensitivity: 'base' });
    });
    if (sorting.order === 'asc') return sorted;
    // Keep urgent pinned at top; reverse only within urgent / non-urgent groups.
    const urgent = sorted.filter((item) => isUrgentRequestType(item?.type)).reverse();
    const rest = sorted.filter((item) => !isUrgentRequestType(item?.type)).reverse();
    return [...urgent, ...rest];
  }, [eventRequests, sorting.key, sorting.order, resourceById]);

  const filteredEventRequests = useMemo(
    () => orderedEventRequests.filter((item) => filterRequestsFun(item, activeTabStatus, keyword)),
    [
      orderedEventRequests,
      activeTabStatus,
      keyword,
      requestTypeFilter,
      transportationSupportFilter,
      newPatientFilter,
      resourceById,
    ]
  );
  const perPage = Math.max(5, parseInt(itemsPerPage, 10) || REQUESTS_PER_PAGE_DEFAULT);
  const pageCount = Math.ceil((filteredEventRequests.length || 0) / perPage);
  const safeOffset = itemOffset >= filteredEventRequests.length ? 0 : itemOffset;
  const pagedEventRequests = filteredEventRequests.slice(safeOffset, safeOffset + perPage);

  useEffect(() => {
    setItemOffset(0);
    setCurrentPage(1);
  }, [keyword, requestTypeFilter, transportationSupportFilter, newPatientFilter]);

  useEffect(() => {
    if (itemOffset !== 0 && itemOffset >= (filteredEventRequests.length || 0)) {
      setItemOffset(0);
      setCurrentPage(1);
    }
  }, [filteredEventRequests.length, itemOffset]);

  const handlePageClick = (event) => {
    const filteredLength = filteredEventRequests.length || 1;
    const newOffset = (event.selected * perPage) % filteredLength;
    setItemOffset(newOffset);
    setCurrentPage(event.selected + 1);
  };

  const handlePageSelect = (pageNo) => {
    const parsed = parseInt(pageNo, 10);
    if (!Number.isFinite(parsed) || parsed < 1) return;
    const maxPage = Math.max(1, pageCount);
    const nextPage = Math.min(parsed, maxPage);
    const filteredLength = filteredEventRequests.length || 1;
    setItemOffset(((nextPage - 1) * perPage) % filteredLength);
    setCurrentPage(nextPage);
  };

  const exportColumns = useMemo(() => {
    const withAccessors = columns.map((col) => {
      switch (col.key) {
        case 'type':
          return { ...col, accessor: (row) => formatRequestTypeDisplay(row?.type) };
        case 'create_date':
          return { ...col, accessor: (row) => (row?.create_date ? new Date(row.create_date).toLocaleDateString() : '') };
        case 'status':
          return { ...col, accessor: (row) => getStatusLabel(row?.status) };
        case 'source':
          return { ...col, accessor: (row) => sourceLabelByValue[row?.source] || row?.source || '' };
        case 'provider':
          return { ...col, accessor: (row) => getProviderDisplay(row) };
        case 'provider_type':
          return { ...col, accessor: (row) => getProviderTypeDisplay(row) };
        case 'provider_phone':
          return { ...col, accessor: (row) => getProviderPhoneDisplay(row) };
        case 'provider_address':
          return { ...col, accessor: (row) => getProviderAddressDisplay(row) };
        default:
          return col;
      }
    });
    if (!showDone) return withAccessors;
    return [
      ...withAccessors,
      {
        key: 'complete_by',
        label: 'Complete By',
        show: true,
        accessor: (row) => row?.edit_history?.[row.edit_history.length - 1]?.employee || '',
      },
    ];
  }, [columns, showDone, sourceLabelByValue, resourceById]);
  const visibleColumns = useMemo(() => columns.filter((col) => col.show), [columns]);
  const showCol = useMemo(() => {
    const flags = {};
    columns.forEach((col) => {
      flags[col.key] = !!col.show;
    });
    return flags;
  }, [columns]);
  const canEditRequests = AuthService.canEditAppointmentRequests();
  const tableWrapRef = usePageScrollPinnedTableHeader([
    pagedEventRequests.length,
    visibleColumns,
    activeTabStatus,
    keyword,
    perPage,
    safeOffset,
  ]);
  const requestTypeOptions = [
    ...new Set(
      eventRequests
        .map((item) => formatRequestTypeDisplay(item?.type))
        .filter(Boolean)
        .filter((option) => option !== 'Option 6')
    ),
  ];
  const transportationSupportOptions = [...new Set(eventRequests.map((item) => `${item?.transportation || ''}`.trim()).filter(Boolean))];
  const newPatientOptions = [...new Set(eventRequests.map((item) => `${item?.np || ''}`.trim()).filter(Boolean))];

  const cleanFilterAndClose = () => {
    setRequestTypeFilter('');
    setTransportationSupportFilter('');
    setNewPatientFilter('');
    setShowFilterDropdown(false);
  };

  const applyFilterAndClose = () => {
    setShowFilterDropdown(false);
  };

  const customFilterMenu = React.forwardRef(
    ({ children, style, className, 'aria-labelledby': labeledBy }, ref) => (
      <div
        ref={ref}
        style={style}
        className={className}
        aria-labelledby={labeledBy}
      >
        <h6>Filter By</h6>
        <div className="app-main-content-fields-section margin-sm">
          <div className="me-4">
            <div className="field-label">Request Type</div>
            <select value={requestTypeFilter} onChange={(e) => setRequestTypeFilter(e.currentTarget.value)}>
              <option value="">All</option>
              {requestTypeOptions.map((option) => <option key={option} value={option}>{option}</option>)}
            </select>
          </div>
        </div>
        <div className="app-main-content-fields-section margin-sm">
          <div className="me-4">
            <div className="field-label">Transportation Support</div>
            <select value={transportationSupportFilter} onChange={(e) => setTransportationSupportFilter(e.currentTarget.value)}>
              <option value="">All</option>
              {transportationSupportOptions.map((option) => <option key={option} value={option}>{EventsService.getMedicalTransportationSupportDisplay(option)}</option>)}
            </select>
          </div>
        </div>
        <div className="app-main-content-fields-section margin-sm">
          <div className="me-4">
            <div className="field-label">New Patient</div>
            <select value={newPatientFilter} onChange={(e) => setNewPatientFilter(e.currentTarget.value)}>
              <option value="">All</option>
              {newPatientOptions.map((option) => <option key={option} value={option}>{option}</option>)}
            </select>
          </div>
        </div>
        <div className="list row">
          <div className="col-md-12">
            <button className="btn btn-default btn-sm float-right" onClick={() => cleanFilterAndClose()}> Cancel </button>
            <button className="btn btn-primary btn-sm float-right" onClick={() => applyFilterAndClose()}> Filter </button>
          </div>
        </div>
      </div>
    )
  );
  
  const table = (
    <div className="list row mb-4">
    <div
      ref={tableWrapRef}
      className="col-md-12 event-request-table-wrap sticky-table-scroll sticky-table-scroll--appointment-request"
    >
      <table className="personnel-info-table"> 
        <thead>
          <tr>
            <th className="th-index">No.</th>
            {
              visibleColumns.map((column) => <th className="sortable-header" key={column.key}>
                {column.label}  <span className="float-right" onClick={() => sortTableWithField(column.key)}><img src={`/images/${getSortingImg(column.key)}.png`} alt=""></img></span>
              </th>)
            }
            {showDone && <th>Complete By</th>}
            <th>Comments</th>
            <th>Comment on the record</th>
            <th></th>
          </tr>
          
        </thead>
        <tbody>
          {
            pagedEventRequests.map((eventRequest, index) => <tr key={eventRequest?.id} className={isUrgentRequestType(eventRequest?.type) ? 'event-request-row-urgent' : undefined}>
              <td className="td-index">{safeOffset + index + 1}</td>
              {showCol.customer_display && <td>{eventRequest?.customer_display}</td>}
              {showCol.source && <td>{sourceLabelByValue[eventRequest?.source] || eventRequest?.source}</td>}
              {showCol.type && <td>{formatRequestTypeDisplay(eventRequest?.type)}</td>}
              {showCol.provider && <td>{getProviderDisplay(eventRequest)}</td>}
              {showCol.provider_type && <td>{getProviderTypeDisplay(eventRequest)}</td>}
              {showCol.provider_phone && <td>{getProviderPhoneDisplay(eventRequest)}</td>}
              {showCol.provider_address && <td>{getProviderAddressDisplay(eventRequest)}</td>}
              {showCol.transportation && <td>{EventsService.getMedicalTransportationSupportDisplay(eventRequest?.transportation) || '-'}</td>}
              {showCol.symptom && <td>{eventRequest?.symptom}</td>}
              {showCol.np && <td>{eventRequest?.np}</td>}
              {showCol.upload && <td>{eventRequest?.upload}</td>}
              {showCol.status && <td>{getStatusLabel(eventRequest?.status)}</td>}
              {showCol.create_date && <td>{eventRequest?.create_date ? new Date(eventRequest.create_date).toLocaleDateString() : ''}</td>}
              {showDone && <td>{eventRequest?.edit_history && eventRequest?.edit_history[eventRequest?.edit_history?.length - 1]?.employee || ''}</td>}
              <td>{eventRequest?.notes?.map((note, noteIndex) => {
                return <p key={`${eventRequest?.id}-note-${noteIndex}`}>{`${note?.author}: ${note?.content}`}</p>
              })}</td>
              <td align="center"><textarea id={eventRequest?.id} name={eventRequest?.id} className="comments-text" disabled={!canEditRequests} /> {canEditRequests && <button className="btn btn-primary btn-sm me-2 mt-2" onClick={() => saveComments(eventRequest?.id)}>Submit</button>}</td>
              <td className={eventRequest.status === 'active' ? 'table-button-container': ''}>
                {eventRequest.status !== 'done' && canEditRequests && <button className="btn btn-primary btn-sm me-2" onClick={() => createNewEvent(eventRequest)}> New Appointment </button>}
                {eventRequest.status !== 'done' && canEditRequests && <button className="btn btn-secondary btn-sm me-2" onClick={() => confirmEventRequest(eventRequest)}> Completed</button>}
              </td>
            </tr>)
          }
        </tbody>
      </table>
    </div>
      <div className="col-md-12 pagination-container">
        {pageCount > 0 && (
        <ReactPaginate
          key={`${perPage}-${activeTabStatus}`}
          forcePage={Math.max(0, currentPage - 1)}
          className="customers-pagination"
          breakLabel="..."
          nextLabel="Next"
          onPageChange={handlePageClick}
          pageRangeDisplayed={5}
          pageCount={pageCount}
          previousLabel="Prev"
          renderOnZeroPageCount={null}
          containerClassName="pagination justify-content-center"
          pageClassName="page-item"
          pageLinkClassName="page-link"
          previousClassName="page-prev-item"
          previousLinkClassName="page-link"
          nextClassName="page-next-item"
          nextLinkClassName="page-link"
          activeClassName="active"
          breakClassName="page-item"
          breakLinkClassName="page-link"
        />
        )}
        <div className="select-page-container">
          <input
            type="number"
            className="page-picker"
            max={Math.max(1, pageCount)}
            min={1}
            value={currentPage}
            onChange={(e) => handlePageSelect(e.target.value)}
          />
          <div className="per-page-label">{` of ${Math.max(pageCount, 1)}`}</div>
        </div>
        <div className="select-page-container">
          <select className="per-page" value={perPage} onChange={(e) => {
            setItemsPerPage(e.target.value);
            setItemOffset(0);
            setCurrentPage(1);
          }}>
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select> <span className="per-page-label"> /page</span>
        </div>
      </div>
  </div>
  );


  return (
    <>
      <div className="list row mb-4">
        <Breadcrumb>
          <Breadcrumb.Item href="/dashboard/dashboard">Medical</Breadcrumb.Item>
          <Breadcrumb.Item active>
            Appointment Requests Information
          </Breadcrumb.Item>
        </Breadcrumb>
        <div className="col-md-12 text-primary">
           <h4>
             All Medical Appointment Requests  
             {/* <button className="btn btn-primary btn-sm" onClick={() => {goToCreateNew()}}>Create New Customer</button> 
             <button className="btn btn-link btn-sm" onClick={() => {redirectToAdmin()}}>Back</button> */}
           </h4> 
        </div>
      </div>
      <div className="app-main-content-list-container list-page event-request-list-page">
				<div className="app-main-content-list-func-container">
					<Tabs
						activeKey={showDone ? 'completedRequest' : 'activeRequests'}
						id="requests-tab"
						mountOnEnter
						unmountOnExit
						onSelect={(k) => showDoneItems(k)}
					>
						<Tab eventKey="activeRequests" title="Active Requests">
							{!showDone ? table : null}
						</Tab>
						<Tab eventKey="completedRequest" title="Completed Requests">
							{showDone ? table : null}
						</Tab>
					</Tabs>
					<div className="list-func-panel">
						<input className="me-2 with-search-icon" type="text" placeholder="Search" value={keyword}  onChange={(e) => setKeyword(e.currentTarget.value)} />
            <Dropdown
              key={'filter-event-request-list'}
              id="filter-event-request-list"
              className="me-2"
              show={showFilterDropdown}
              onToggle={(isOpen) => setShowFilterDropdown(isOpen)}
              autoClose={false}
            > 
              <Dropdown.Toggle variant="outline-secondary">
                <Filter size={16} className="me-2"></Filter>Filter
              </Dropdown.Toggle>
              <Dropdown.Menu as={customFilterMenu}/> 
            </Dropdown>
						<ManageTable columns={columns} onColumnsChange={setColumns} />
						{AuthService.canEditAppointmentRequests() && <button className="btn btn-primary me-2" onClick={() => goToCreateNew()}><Plus size={16}></Plus>Add Request</button>}
						<Export 
							columns={exportColumns} 
							data={filteredEventRequests} 
							filename="event-requests"
							actionLogPageName="Medical Appointment Requests"
						/>
					</div>
				</div>
      </div>
    </>
  )
};

export default EventRequestList;